import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

/** Sous-ensemble non-standard de la Network Information API (Chromium/Edge/Android WebView uniquement). */
interface NetworkInformation {
  saveData?: boolean;
  effectiveType?: string;
}

const SLOW_CONNECTION_TYPES = new Set(['slow-2g', '2g']);
/** Laisse l'hydratation et le premier rendu respirer avant de solliciter le réseau pour des routes
 * non demandées — évite de faire concurrence à ce qui charge réellement la page courante. */
const PRELOAD_DELAY_MS = 2000;

/**
 * Précharge en arrière-plan les chunks des routes paresseuses (une fois la navigation initiale
 * stabilisée) pour que les clics suivants soient instantanés, sans jamais imposer ce coût réseau
 * aux utilisateurs en Data Saver ou sur une connexion lente (2G) — public cible majoritairement
 * mobile et sensible au coût des données. Volontairement globale (toutes les routes, pas de flag
 * `data: { preload: true }` par route à maintenir) : reste correcte automatiquement pour toute
 * nouvelle route ajoutée, sans risque d'oubli.
 */
@Injectable({ providedIn: 'root' })
export class NetworkAwarePreloadingStrategy implements PreloadingStrategy {
  preload(_route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (this.shouldSkipPreload()) return of(null);
    return timer(PRELOAD_DELAY_MS).pipe(mergeMap(() => load()));
  }

  private shouldSkipPreload(): boolean {
    if (typeof navigator === 'undefined') return true; // Rendu serveur : jamais de préchargement.

    const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
    if (!connection) return false; // API non supportée (Safari, Firefox…) : préchargement par défaut.
    if (connection.saveData) return true;
    return !!connection.effectiveType && SLOW_CONNECTION_TYPES.has(connection.effectiveType);
  }
}
